import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import Tag from '../shared/Tag';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { toCsv } from '../../utils/csv.js';
import { canAccess } from '../../utils/rbac';

// Reconciliation of the tracker's "Remaining" (contract total − Invoiced/Paid,
// floored at £0) against the milestone view. For each live SOW:
//   Remaining = Awaiting + Ready to bill + Gap
// where Gap is contract value with no live milestone behind it. The book only
// ties up when every SOW's Gap is £0, so this page lists the SOWs that don't,
// biggest first, so the difference can be chased down and cleared.

const STATUS_TABS = ['Active + On Hold', 'Active', 'On Hold'];

// Classify why a SOW's Remaining doesn't equal Awaiting + Ready to bill.
function classify(s) {
  const remaining = s.remaining_to_invoice || 0;
  const awaiting  = s.awaiting_sum || 0;
  const ready     = s.ready_to_bill_sum || 0;
  const gap       = remaining - awaiting - ready;
  const liveCount = s.milestone_count || 0; // Awaiting/Ready/Invoiced/Paid (excl Cancelled/Split/Credit Noted)
  const cancelled = s.cancelled_sum || 0;
  if (Math.abs(gap) <= 1) return { gap, key: 'ok', label: 'Reconciles', cls: 'bg-emerald-100 text-emerald-700' };
  if (gap > 1) {
    if (liveCount === 0) return { gap, key: 'none', label: 'No milestones', cls: 'bg-red-100 text-red-700' };
    if (cancelled > 1)   return { gap, key: 'cancelled', label: 'Cancelled value in total', cls: 'bg-amber-100 text-amber-700' };
    return { gap, key: 'under', label: 'Milestones under contract', cls: 'bg-amber-100 text-amber-700' };
  }
  // gap < -1 : Awaiting+Ready exceed the contract remaining
  return { gap, key: 'over', label: 'Milestones exceed remaining', cls: 'bg-purple-100 text-purple-700' };
}

const REASON_HELP = {
  none:      'SOW is live but has no milestones at all — the whole remaining is unscheduled. Add a billing schedule.',
  under:     'The milestones entered do not add up to the remaining contract value — some milestones are missing. Add the missing ones.',
  cancelled: 'Cancelled milestone value is still sitting in the SOW total, inflating remaining. Raise a CCN to reduce the total, or reinstate the milestones.',
  over:      'Awaiting/Ready-to-bill milestones add up to more than the remaining contract value — likely over-milestoned or the SOW has been over-invoiced. Check the total and milestones.',
  ok:        'Remaining equals Awaiting + Ready to bill — this SOW ties up.',
};

export default function ReconciliationPage() {
  const { navigate } = useApp();
  const [sows, setSows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Active + On Hold');
  const [onlyOff, setOnlyOff] = useState(true);
  const [search, setSearch] = useState('');
  const seesMoney = canAccess('sow:see-financials');

  const load = () => {
    if (!seesMoney) return;
    setLoading(true);
    api.get('sows').then(rows => { setSows(Array.isArray(rows) ? rows : []); setLoading(false); });
  };
  useEffect(load, []);
  useRefreshOnFocus(load);

  // Live book: which SOW statuses this page reconciles.
  const book = useMemo(() => {
    const wanted = tab === 'Active' ? ['Active'] : tab === 'On Hold' ? ['On Hold'] : ['Active', 'On Hold'];
    return sows
      .filter(s => wanted.includes(s.status))
      .map(s => ({ ...s, _c: classify(s) }));
  }, [sows, tab]);

  const totals = useMemo(() => {
    const t = { remaining: 0, awaiting: 0, ready: 0, gap: 0, off: 0 };
    for (const s of book) {
      t.remaining += s.remaining_to_invoice || 0;
      t.awaiting  += s.awaiting_sum || 0;
      t.ready     += s.ready_to_bill_sum || 0;
      t.gap       += s._c.gap;
      if (s._c.key !== 'ok') t.off += 1;
    }
    return t;
  }, [book]);

  const q = search.trim().toLowerCase();
  const rows = useMemo(() => {
    let r = onlyOff ? book.filter(s => s._c.key !== 'ok') : book;
    if (q) r = r.filter(s =>
      (s.reference || '').toLowerCase().includes(q) ||
      (s.client_name || '').toLowerCase().includes(q) ||
      (s.detail || '').toLowerCase().includes(q) ||
      (s.owner_name || '').toLowerCase().includes(q)
    );
    // Biggest absolute gap first — that's where the money is.
    return [...r].sort((a, b) => Math.abs(b._c.gap) - Math.abs(a._c.gap));
  }, [book, onlyOff, q]);

  const downloadCSV = () => {
    const headers = ['Ref', 'Status', 'Client', 'Detail', 'PM', 'Contract excl VAT', 'Invoiced', 'Remaining', 'Awaiting', 'Ready to bill', 'Gap', 'Reason'];
    const dataRows = rows.map(s => [
      s.reference, s.status, s.client_name || '', s.detail || '', s.owner_name || '',
      s.total_excl_vat || 0, s.invoiced_sum || 0, s.remaining_to_invoice || 0,
      s.awaiting_sum || 0, s.ready_to_bill_sum || 0, s._c.gap.toFixed(2), s._c.label,
    ]);
    const csv = toCsv(headers, dataRows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `remaining-reconciliation-${tab.toLowerCase().replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Stat = ({ label, value, color, help }) => (
    <div className="border border-gray-200 rounded-lg p-3 bg-white" title={help}>
      <div className="text-[8px] font-bold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-lg font-extrabold ${color}`}>{formatMoney(value)}</div>
    </div>
  );

  // Remaining Reconciliation is SOW values and nothing else, and the API sends those only to
  // holders of sow:see-financials (pen test AD26341, AD12/AD16). Without it the
  // page says where the figures come from rather than showing every SOW at £0.
  if (!seesMoney) return (
    <div className="fade-in bg-white border border-gray-200 rounded-lg p-4 text-[10px] text-gray-600">
      These figures are SOW values, which come with the "See SOW values, rates, costs and contractor pay" access. An administrator can grant it in Admin → Access.
    </div>
  );

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Remaining Reconciliation</h2>
          <div className="text-[9px] text-gray-500">
            Where the Tracker "Remaining" and the Milestones view differ, SOW by SOW
          </div>
        </div>
        <button onClick={downloadCSV} disabled={!rows.length}
          className="px-3 py-1.5 bg-white border border-gray-200 text-navy text-[10px] font-semibold rounded hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap">↓ Export CSV</button>
      </div>

      {/* The bridge: Remaining = Awaiting + Ready to bill + Gap */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <Stat label="Remaining (contract)" value={totals.remaining} color="text-amber-600"
          help="Sum of Remaining across the selected book: contract total − Invoiced/Paid, floored at £0." />
        <Stat label="Awaiting" value={totals.awaiting} color="text-gray-700"
          help="Milestones in Awaiting status." />
        <Stat label="Ready to bill" value={totals.ready} color="text-gray-700"
          help="Milestones ready to bill — delivery done, invoice pending. Kept separate from Awaiting." />
        <Stat label="Unreconciled gap" value={totals.gap} color={Math.abs(totals.gap) > 1 ? 'text-red-600' : 'text-emerald-600'}
          help="Remaining − Awaiting − Ready to bill. Contract value with no live milestone behind it. This is the difference to close." />
      </div>
      <div className="mb-3 text-[9px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5">
        <span className="font-semibold text-navy">{formatMoney(totals.remaining)}</span> remaining
        <span className="text-gray-300 mx-1">=</span>
        {formatMoney(totals.awaiting)} awaiting
        <span className="text-gray-300 mx-1">+</span>
        {formatMoney(totals.ready)} ready to bill
        <span className="text-gray-300 mx-1">+</span>
        <span className={Math.abs(totals.gap) > 1 ? 'text-red-600 font-semibold' : ''}>{formatMoney(totals.gap)} unreconciled</span>
        <span className="text-gray-400"> across {totals.off} SOW{totals.off !== 1 ? 's' : ''}. Close the gap by adding missing milestones or correcting SOW totals.</span>
      </div>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {STATUS_TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-2.5 py-1 text-[9px] font-semibold rounded-md ${tab === t ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-200'}`}>{t}</button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-[9px] font-semibold text-gray-600 cursor-pointer">
          <input type="checkbox" className="w-3 h-3 accent-gold" checked={onlyOff} onChange={e => setOnlyOff(e.target.checked)} />
          Only SOWs that don't reconcile
        </label>
        <input className="form-input text-[10px] py-1 max-w-[220px] ml-auto"
          placeholder="Search ref, client, PM, detail…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-gray-50">
            {['Ref', 'Status', 'Client', 'PM', 'Contract', 'Invoiced', 'Remaining', 'Awaiting', 'Ready', 'Gap', 'Reason'].map((h, i) => (
              <th key={h} className={`px-2 py-1 text-[8px] uppercase font-bold border-b text-gray-500 ${i >= 4 && i <= 9 ? 'text-right' : 'text-left'}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.id} className="hover:bg-gold/5 cursor-pointer border-b border-gray-50" onClick={() => navigate('sow-detail', s.id)}>
                <td className="px-2 py-1 text-[10px] font-bold text-gold whitespace-nowrap">{s.reference}</td>
                <td className="px-2 py-1"><Tag status={s.status} /></td>
                <td className="px-2 py-1 text-[9px] max-w-[120px] truncate">{s.client_name || '-'}</td>
                <td className="px-2 py-1 text-[9px] whitespace-nowrap">{s.owner_name || '-'}</td>
                <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap">{formatMoney(s.total_excl_vat)}</td>
                <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap text-gray-500">{formatMoney(s.invoiced_sum)}</td>
                <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap font-semibold text-amber-600">{formatMoney(s.remaining_to_invoice)}</td>
                <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap">{formatMoney(s.awaiting_sum)}</td>
                <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap">{formatMoney(s.ready_to_bill_sum)}</td>
                <td className={`px-2 py-1 text-[10px] text-right whitespace-nowrap font-bold ${Math.abs(s._c.gap) > 1 ? (s._c.gap > 0 ? 'text-red-600' : 'text-purple-600') : 'text-emerald-600'}`}>{formatMoney(s._c.gap)}</td>
                <td className="px-2 py-1 whitespace-nowrap" title={REASON_HELP[s._c.key]}>
                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${s._c.cls}`}>{s._c.label}</span>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="text-center py-6 text-[10px] text-gray-400">
                {onlyOff ? 'Everything reconciles in this book 🎉' : 'No SOWs found'}
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={11} className="text-center py-6 text-[10px] text-gray-400">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[8px] text-gray-400">
        Gap = Remaining − Awaiting − Ready to bill. Positive = contract value with no live milestone (add milestones or reduce the total). Negative = milestones exceed the remaining (check for over-billing). Cancelled &amp; Expired SOWs are excluded.
      </div>
    </div>
  );
}
